import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-sand grain py-20" data-testid="admin-reset-page">
      <div className="mx-auto max-w-md px-6">
        <Reveal>
          <div className="rounded-[2rem] border border-coal/5 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(26,29,36,0.35)]">
            <Eyebrow>Team access</Eyebrow>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-coal">Choose a new password</h1>
            {done ? (
              <div className="mt-5" data-testid="reset-success">
                <p className="text-sm text-coal-muted">Your password has been updated. You can log in with it now.</p>
                <Link
                  to="/admin/login"
                  data-testid="reset-login-link"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal"
                >
                  Go to login
                </Link>
              </div>
            ) : (
              <>
                {!token && (
                  <p className="mt-4 rounded-xl bg-ember-subtle px-4 py-3 text-sm font-semibold text-ember" data-testid="reset-no-token">
                    This reset link is missing its token. Please use the link from your email.
                  </p>
                )}
                {error && (
                  <p className="mt-4 rounded-xl bg-ember-subtle px-4 py-3 text-sm font-semibold text-ember" data-testid="reset-error">
                    {error}
                  </p>
                )}
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="new-password" className="mb-1.5 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">
                      New password
                    </label>
                    <input
                      id="new-password"
                      data-testid="reset-password-input"
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-coal/15 bg-sand px-4 py-3 text-sm text-coal outline-none transition-colors focus:border-teal"
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className="mb-1.5 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">
                      Confirm password
                    </label>
                    <input
                      id="confirm-password"
                      data-testid="reset-confirm-input"
                      type="password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full rounded-xl border border-coal/15 bg-sand px-4 py-3 text-sm text-coal outline-none transition-colors focus:border-teal"
                      placeholder="Repeat it"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy || !token}
                    data-testid="reset-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-ember px-6 py-3 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal disabled:opacity-60"
                  >
                    <KeyRound size={16} />
                    {busy ? "Updating..." : "Update password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
